import React from "react";
import { inject, observer } from "mobx-react";
import { PolicyStore } from "../../../stores/policyStore";
import { Button } from "@material-ui/core";
import Header from "../../sharedComponents/header/header";
import DropDown from "../../sharedComponents/dropDown/dropDown";
import { PolicyRenewalSeriesDropDown } from "../../../helpers/policy";
import { RetrieveCalculationService } from "../../../services/services/retrieveCalculationService";
import "./renewalContract.scss";
import { ErrorMessage } from "../../../helpers/errorMessage";
import TextInput from "../../sharedComponents/textInput/textInput";
import { HttpError } from "../../sharedComponents/notification/httpError";

interface RenewalContractProps {
  policyStore?: PolicyStore;
  retrieveCalculationService?: RetrieveCalculationService;
}

interface RenewalContractState {
  displayRenewalForm: boolean;
}

@inject("policyStore", "retrieveCalculationService")
@observer
export default class RenewalContract extends React.Component<RenewalContractProps, RenewalContractState> {
  constructor(props: RenewalContractProps) {
    super(props);

    this.state = {
      displayRenewalForm: false
    };
  }

  render() {
    return (
      <div className="renewal-contract">
        {
          this.contractForRenewalRetrieved()
            ? this.renderReadOnlyFrame()
            : this.state.displayRenewalForm
              ? this.renderInputFrame()
              : this.renderRenewalButton()
        }
      </div>
    );
  }

  private renderRenewalButton() {
    return (
      <Button variant="contained"
        className="button renewal-button"
        onClick={() => {
          this.setState({ displayRenewalForm: true });
        }}
      >
        Wznowienie do polisy
      </Button>
    );
  }

  private renderReadOnlyFrame() {
    return (
      <div className="frame">
        <Header type="medium" mainHeader="Wznowienie do polisy" />

        {this.isError() && (
          <HttpError
            type="http"
            defaultErrorMessage="Nie udało się wczytać wznowienia do polisy"
            requestStatuses={this.getRequestStatuses()}
          />
        )}

        <div className="content">
          <div className="input-frame">
            <div className="input-row">
              {this.renderSeries(true)}
              {this.renderNumber(true)}
              <div className="field-wrapper">
                <div className="label">&nbsp;</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  private renderInputFrame() {
    return (
      <div className="frame">
        <Header type="medium" mainHeader="Wznowienie do polisy" />

        {this.isError() && (
          <HttpError
            type="http"
            defaultErrorMessage="Nie udało się wczytać wznowienia do polisy"
            requestStatuses={this.getRequestStatuses()}
          />
        )}

        <div className="content">
          <div className="input-frame">
            <div className="input-row">
              <div className="field-wrapper">
                <div className="label">Seria</div>
                <DropDown
                  onChange={newValue => {
                    this.props.policyStore!.setSeries(newValue);
                  }}
                  value={this.props.policyStore!.policyRenewal.series}
                  items={PolicyRenewalSeriesDropDown}
                />
              </div>
              <div className="field-wrapper">
                <div className="label">Numer</div>
                <TextInput
                  onChange={newValue => {
                    this.props.policyStore!.setNumber(newValue);
                  }}
                  validatedValue={
                    this.props.policyStore!.policyRenewal.number
                  }
                />
              </div>
              <div className="field-wrapper">
                <div className="label">
                  Podaj PESEL lub REGON jednego z ubezpieczonych/ubezpieczających na wznawianej polisie
                </div>
                <TextInput
                  onChange={newValue => {
                    this.props.policyStore!.setPeselOrRegon(newValue);
                  }}
                  validatedValue={
                    this.props.policyStore!.policyRenewal.peselOrRegon
                  }
                  onlyNumberValue={true}
                />
              </div>
            </div>
            <div className="button-row">
              <Button
                variant="contained"
                className="button cancel-button"
                onClick={() => this.setState({ displayRenewalForm: false })}
              >
                Anuluj
              </Button>
              <Button
                variant="contained"
                className="button load-button"
                onClick={() => this.retrieveContractButtonOnClick()}
              >
                Wczytaj
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  private getRequestStatuses() {
    return [
      this.props.retrieveCalculationService!
        .retrieveContractForRenewalBySeriesAndNumberRequestStatus,
      this.props.retrieveCalculationService!.retrieveContractForRenewalByIdRequestStatus
    ];
  }

  private isError() {
    const retrieveCalculationService = this.props.retrieveCalculationService!;
    const retrieveContractBySeriesAndNumberHasError =
      retrieveCalculationService
        .retrieveContractForRenewalBySeriesAndNumberRequestStatus
        .isError;
    const retrieveContractByIdHasError =
      retrieveCalculationService.retrieveContractForRenewalByIdRequestStatus.isError;

    return retrieveContractBySeriesAndNumberHasError || retrieveContractByIdHasError;
  }

  private async retrieveContractButtonOnClick() {
    this.props.policyStore!.policyRenewal.number.validate();
    this.props.policyStore!.policyRenewal.peselOrRegon.validate();

    if (!ErrorMessage.verifyIfFormErrorMessageIsRendered()) {
      await this.retrieveContractBySeriesAndNumber();
    }
  }

  private contractForRenewalRetrieved() {
    const retrieveCalculationService = this.props.retrieveCalculationService!;
    const retrieveContractBySeriesAndNumberRequestStatus =
      retrieveCalculationService
        .retrieveContractForRenewalBySeriesAndNumberRequestStatus;
    const retrieveContractBySeriesAndNumberWithSuccess =
      retrieveContractBySeriesAndNumberRequestStatus.isDone &&
      !retrieveContractBySeriesAndNumberRequestStatus.isError;
    const retrieveContractByIdRequestStatus =
      retrieveCalculationService.retrieveContractForRenewalByIdRequestStatus;
    const retrieveContractByIdWithSuccess =
      retrieveContractByIdRequestStatus.isDone &&
      !retrieveContractByIdRequestStatus.isError;

    return (
      this.props.policyStore!.isPolicyRenewal &&
      (retrieveContractBySeriesAndNumberWithSuccess ||
        retrieveContractByIdWithSuccess)
    );
  }

  private async retrieveContractBySeriesAndNumber() {
    await this.props.retrieveCalculationService!
      .retrieveContractForRenewalBySeriesAndNumberRequest(
        this.props.policyStore!.policyRenewal
      );
  }

  private renderSeries(disabled: boolean) {
    return (
      <div className="field-wrapper">
        <div className="label">Seria</div>
        <DropDown
          onChange={newValue => {
            this.props.policyStore!.setSeries(newValue);
          }}
          value={this.props.policyStore!.policyRenewal.series}
          items={PolicyRenewalSeriesDropDown}
          disabled={disabled}
        />
      </div>
    );
  }

  private renderNumber(disabled: boolean) {
    return (
      <div className="field-wrapper">
        <div className="label">Numer</div>
        <TextInput
          onChange={newValue => {
            this.props.policyStore!.setNumber(newValue);
          }}
          validatedValue={this.props.policyStore!.policyRenewal.number}
          disabled={disabled}
        />
      </div>
    );
  }
}
